/**
 * useAnalytics - Analitik araçları kullanmak için hook
 */
import { useCallback } from 'react';
import eventManager from '../utils/analytics/EventManager';

/**
 * Analitik araçları kullanmak için hook
 * @returns {Object} Analitik fonksiyonları
 */
const useAnalytics = () => {
  /**
   * Event gönderir
   * @param {string} eventName - Event adı
   * @param {Object} eventParams - Event parametreleri
   */
  const trackEvent = useCallback((eventName, eventParams = {}) => {
    const page_path = window.location.pathname;
    const page_title = document.title;
    eventManager.trackEvent(eventName, {
      ...eventParams,
      page_path,
      page_title,
    });
    
    // Belirli eventleri Google Ads dönüşümüne dönüştür
    try {
      if (typeof window.sendGoogleAdsConversion === 'function') {
        // Form gönderimi ve randevu tamamlama olaylarını dönüşüme dönüştür
        if (eventName === 'form_submit' || eventName === 'form_submission') {
          window.sendGoogleAdsConversion(
            null, // Default Google Ads ID kullan
            'form_submission',
            eventParams.value || 100
          );
        } else if (eventName === 'appointment_complete') {
          window.sendGoogleAdsConversion(
            null, // Default Google Ads ID kullan
            'appointment_complete',
            eventParams.value || 150
          );
        } else if (eventName === 'contact_phone_click') {
          // Telefon araması dönüşüm izleme
          window.sendGoogleAdsConversion(
            null, // Default Google Ads ID kullan
            'phone_call',  // Telefon araması için conversion label
            eventParams.value || 50  // Değer (TL cinsinden)
          );
          console.log('Google Ads telefon araması dönüşümü gönderildi');
        } else if (eventName === 'purchase' || eventName === 'lead') {
          window.sendGoogleAdsConversion(
            null, // Default Google Ads ID kullan
            eventParams.conversion_label || eventName,
            eventParams.value || 0
          );
        }
      }
    } catch (error) {
      console.warn('Google Ads dönüşümü gönderilirken hata oluştu:', error);
    }
  }, []);
  
  /**
   * Kullanıcı kimliği ayarlar
   * @param {string} userId - Kullanıcı kimliği
   */
  const setUserId = useCallback((userId) => {
    eventManager.setUserId(userId);
  }, []);
  
  /**
   * Kullanıcı özelliklerini ayarlar
   * @param {Object} properties - Kullanıcı özellikleri
   */
  const setUserProperties = useCallback((properties) => {
    eventManager.setUserProperties(properties);
  }, []);

  /**
   * Randevu formunu görüntüleme eventi gönderir
   */
  const trackAppointmentView = useCallback(() => {
    eventManager.trackEvent('appointment_view');
  }, []);

  /**
   * Randevu formunu doldurma başlangıcı eventi gönderir
   */
  const trackAppointmentStart = useCallback(() => {
    eventManager.trackEvent('appointment_start');
  }, []);

  /**
   * Randevu formunu tamamlama eventi gönderir
   * @param {Object} appointmentData - Randevu verileri
   */
  const trackAppointmentComplete = useCallback((appointmentData = {}) => {
    eventManager.trackEvent('appointment_complete', appointmentData);
  }, []);

  /**
   * Randevu başarıyla oluşturuldu eventi gönderir
   * @param {Object} appointmentData - Randevu verileri
   */
  const trackAppointmentSuccess = useCallback((appointmentData = {}) => {
    eventManager.trackEvent('appointment_success', appointmentData);
  }, []);

  /**
   * İletişim etkileşimi eventi gönderir
   * @param {string} type - Etkileşim tipi (phone, email, map)
   * @param {string} value - Etkileşim değeri
   */
  const trackContactInteraction = useCallback((type, value) => {
    const eventMap = {
      phone: 'contact_phone_click',
      email: 'contact_email_click',
      map: 'contact_map_click'
    };

    const eventName = eventMap[type] || 'contact_interaction';
    eventManager.trackEvent(eventName, { value });
  }, []);

  /**
   * Hizmet görüntüleme eventi gönderir
   * @param {string} serviceId - Hizmet ID'si
   * @param {string} serviceName - Hizmet adı
   */
  const trackServiceView = useCallback((serviceId, serviceName) => {
    eventManager.trackEvent('service_view', {
      service_id: serviceId,
      service_name: serviceName
    });
  }, []);

  /**
   * Video izleme başlangıcı eventi gönderir
   * @param {string} videoId - Video ID'si
   * @param {string} videoTitle - Video başlığı
   */
  const trackVideoPlay = useCallback((videoId, videoTitle) => {
    eventManager.trackEvent('video_play', {
      video_id: videoId,
      video_title: videoTitle
    });
  }, []);

  /**
   * Video izleme tamamlama eventi gönderir
   * @param {string} videoId - Video ID'si
   * @param {string} videoTitle - Video başlığı
   */
  const trackVideoComplete = useCallback((videoId, videoTitle) => {
    eventManager.trackEvent('video_complete', {
      video_id: videoId,
      video_title: videoTitle
    });
  }, []);

  // Telefon Arama Funnel'ı için fonksiyonlar
  const trackCallButtonClick = useCallback((source) => {
    eventManager.trackEvent('call_btn_clicked', {
      source: source || 'unknown'
    });
  }, []);

  // WhatsApp İletişim eventi
  const trackWhatsAppButtonClick = useCallback((source) => {
    eventManager.trackEvent('whatsapp_btn_clicked', {
      source: source || 'unknown'
    });
  }, []);

  const trackCallStarted = useCallback((phoneNumber, source) => {
    eventManager.trackEvent('call_started', {
      phone_number: phoneNumber,
      source: source || 'unknown'
    });
  }, []);

  const trackCallCanceled = useCallback((phoneNumber, source, durationSeconds) => {
    eventManager.trackEvent('call_canceled', {
      phone_number: phoneNumber,
      source: source || 'unknown',
      duration_seconds: durationSeconds || 0
    });
  }, []);

  // Randevu Formu Funnel'ı için fonksiyonlar
  const trackAppointmentButtonClick = useCallback((source) => {
    eventManager.trackEvent('appointment_btn_clicked', {
      source: source || 'unknown'
    });
  }, []);

  const trackAppointmentFormViewed = useCallback((source) => {
    eventManager.trackEvent('appointment_form_viewed', {
      source: source || 'unknown'
    });
  }, []);

  const trackAppointmentFormSubmitted = useCallback((serviceType, hasPhone, hasEmail) => {
    eventManager.trackEvent('appointment_form_submitted', {
      service_type: serviceType,
      has_phone: hasPhone,
      has_email: hasEmail
    });
  }, []);

  const trackAppointmentFormError = useCallback((errorType, errorMessage) => {
    eventManager.trackEvent('appointment_form_error', {
      error_type: errorType,
      error_message: errorMessage
    });
  }, []);

  const trackAppointmentFormAbandoned = useCallback((lastFieldFilled, completionPercentage) => {
    eventManager.trackEvent('appointment_form_abandoned', {
      last_field_filled: lastFieldFilled,
      completion_percentage: completionPercentage
    });
  }, []);

  // Hizmet İnceleme Funnel'ı için fonksiyonlar
  const trackServiceCardClicked = useCallback((serviceId, serviceName, source) => {
    eventManager.trackEvent('service_card_clicked', {
      service_id: serviceId,
      service_name: serviceName,
      source: source || 'unknown'
    });
  }, []);

  const trackServiceDetailViewed = useCallback((serviceId, serviceName, viewDurationSeconds) => {
    eventManager.trackEvent('service_detail_viewed', {
      service_id: serviceId,
      service_name: serviceName,
      view_duration_seconds: viewDurationSeconds || 0
    });
  }, []);

  const trackServiceCtaClicked = useCallback((serviceId, serviceName, ctaType) => {
    eventManager.trackEvent('service_cta_clicked', {
      service_id: serviceId,
      service_name: serviceName,
      cta_type: ctaType || 'unknown'
    });
  }, []);

  // Blog Okuma Funnel'ı için fonksiyonlar
  const trackBlogCardClicked = useCallback((blogId, blogTitle, source) => {
    eventManager.trackEvent('blog_card_clicked', {
      blog_id: blogId,
      blog_title: blogTitle,
      source: source || 'unknown'
    });
  }, []);

  const trackBlogRead = useCallback((blogId, blogTitle, readDurationSeconds) => {
    eventManager.trackEvent('blog_read', {
      blog_id: blogId,
      blog_title: blogTitle,
      read_duration_seconds: readDurationSeconds || 0
    });
  }, []);

  const trackBlogShared = useCallback((blogId, blogTitle, sharePlatform) => {
    eventManager.trackEvent('blog_shared', {
      blog_id: blogId,
      blog_title: blogTitle,
      share_platform: sharePlatform || 'unknown'
    });
  }, []);

  const trackBlogCtaClicked = useCallback((blogId, blogTitle, ctaType) => {
    eventManager.trackEvent('blog_cta_clicked', {
      blog_id: blogId,
      blog_title: blogTitle,
      cta_type: ctaType || 'unknown'
    });
  }, []);

  return {
    // Genel fonksiyonlar
    trackEvent,
    setUserId,
    setUserProperties,
    
    // Mevcut fonksiyonlar (geriye dönük uyumluluk için)
    trackAppointmentView,
    trackAppointmentStart,
    trackAppointmentComplete,
    trackAppointmentSuccess,
    trackContactInteraction,
    trackServiceView,
    trackVideoPlay,
    trackVideoComplete,
    
    // Telefon Arama Funnel'ı
    trackCallButtonClick,
    trackCallStarted,
    trackCallCanceled,
    
    // WhatsApp İletişim
    trackWhatsAppButtonClick,
    
    // Randevu Formu Funnel'ı
    trackAppointmentButtonClick,
    trackAppointmentFormViewed,
    trackAppointmentFormSubmitted,
    trackAppointmentFormError,
    trackAppointmentFormAbandoned,
    
    // Hizmet İnceleme Funnel'ı
    trackServiceCardClicked,
    trackServiceDetailViewed,
    trackServiceCtaClicked,
    
    // Blog Okuma Funnel'ı
    trackBlogCardClicked,
    trackBlogRead,
    trackBlogShared,
    trackBlogCtaClicked
  };
};

export default useAnalytics; 